import { apiRequest } from '../../api/client';
import type { KasPromoCodeEntry } from '../../components/kasPromoCode';

const unwrap = <T>(promise: Promise<{ data: T }>): Promise<T> => promise.then((e) => e.data);

export type RewardKind = 'Discount' | 'PercentDiscount' | 'Item' | 'BuyOneGetOne' | 'Combo';

/** Hai NHÓM: trần 50% đọc mệnh giá ở đâu (BR-408) và loại nào bắt khai tồn kho — không quyết định hạn mức mỗi hóa đơn. */
export type RewardGroup = 'Money' | 'InKind';

export type RedemptionWindow = 'Month' | 'Lifetime';

export type Weekday =
  | 'Sunday'
  | 'Monday'
  | 'Tuesday'
  | 'Wednesday'
  | 'Thursday'
  | 'Friday'
  | 'Saturday';

export interface VoucherConditions {
  minBillAmount?: number | null;
  maxPerBill?: number | null;
  branchIds: string[];
  validFromHour?: number | null;
  validToHour?: number | null;
  validDaysOfWeek: Weekday[];
  excludedDates: string[];
  fixedExpiresAt?: string | null;
  /**
   * Cho voucher này nằm CHUNG hóa đơn với ưu đãi khác (BR-414). Mặc định false — một hóa đơn một
   * ưu đãi. Phải bật ở CẢ HAI món quà mới có tác dụng, và chỉ có tác dụng khi maxPerBill ≥ 2.
   */
  allowStacking?: boolean;
}

export interface Reward {
  id: string;
  name: string;
  description: string | null;
  imageUrl: string | null;
  pointsCost: number;
  kind: RewardKind;
  discountAmount: number | null;
  /** Tỷ lệ % giảm, chỉ với PercentDiscount. 20 = giảm 20%. */
  discountPercent: number | null;
  /** Trần số tiền giảm — BẮT BUỘC khi có discountPercent. */
  maxDiscountAmount: number | null;
  /** Giá trị quy đổi của quà hiện vật — chỉ để trần 50% đếm được, KHÔNG hiện cho khách. */
  itemValue: number | null;
  validDays: number;
  stockRemaining: number | null;
  chainId: string | null;
  /** Mã khuyến mãi KAS theo chuỗi. Rỗng = chưa khai chuỗi nào. */
  kasPromoCodes: KasPromoCodeEntry[] | null;
  isActive: boolean;
  conditions: VoucherConditions;
  maxRedemptionsPerMember: number | null;
  redemptionWindow: RedemptionWindow;
  saleFrom: string | null;
  saleUntil: string | null;
  issuedCount: number;
  createdAt: string;
}

export interface UpsertRewardPayload {
  name: string;
  description?: string | null;
  imageUrl?: string | null;
  pointsCost: number;
  kind: RewardKind;
  discountAmount?: number | null;
  discountPercent?: number | null;
  maxDiscountAmount?: number | null;
  itemValue?: number | null;
  validDays: number;
  stockRemaining?: number | null;
  chainId?: string | null;
  /**
   * Mã KAS theo chuỗi. Bỏ trường này đi nghĩa là **không đụng** tới mã đã khai; gửi mảng
   * nghĩa là thay thế trọn bộ. Form luôn gửi mảng để đổi phạm vi kéo được mã cũ đi cùng.
   */
  kasPromoCodes?: { chainId: string; code: string }[];
  isActive: boolean;
  conditions?: VoucherConditions | null;
  maxRedemptionsPerMember?: number | null;
  redemptionWindow?: RedemptionWindow;
  saleFrom?: string | null;
  saleUntil?: string | null;
}

export const rewardKeys = {
  all: ['rewards'] as const,
};

export const KIND_LABELS: Record<RewardKind, string> = {
  Discount: 'Voucher tiền',
  PercentDiscount: 'Giảm %',
  Item: 'Món ăn',
  BuyOneGetOne: 'Mua 1 tặng 1',
  Combo: 'Combo',
};

/** Thứ tự dropdown khớp prototype `a-reward`. */
export const KIND_OPTIONS: { value: RewardKind; label: string }[] = [
  { value: 'PercentDiscount', label: KIND_LABELS.PercentDiscount },
  { value: 'Discount', label: KIND_LABELS.Discount },
  { value: 'Item', label: KIND_LABELS.Item },
  { value: 'BuyOneGetOne', label: KIND_LABELS.BuyOneGetOne },
  { value: 'Combo', label: KIND_LABELS.Combo },
];

/**
 * Chip cột Loại encode **nhóm**, không encode loại.
 * Cam = nhóm tiền — đây là phân loại, không phải trạng thái, nên ADR-009 cho dùng cam.
 */
export const KIND_TAG_COLOR: Record<RewardKind, string | undefined> = {
  Discount: 'orange',
  PercentDiscount: 'orange',
  Item: undefined,
  BuyOneGetOne: undefined,
  Combo: undefined,
};

export function rewardGroupOf(kind: RewardKind): RewardGroup {
  return kind === 'Discount' || kind === 'PercentDiscount' ? 'Money' : 'InKind';
}

export const REDEMPTION_WINDOW_LABELS: Record<RedemptionWindow, string> = {
  Month: 'Mỗi tháng',
  Lifetime: 'Trọn đời',
};

export const WEEKDAY_OPTIONS: { value: Weekday; label: string }[] = [
  { value: 'Monday', label: 'Thứ 2' },
  { value: 'Tuesday', label: 'Thứ 3' },
  { value: 'Wednesday', label: 'Thứ 4' },
  { value: 'Thursday', label: 'Thứ 5' },
  { value: 'Friday', label: 'Thứ 6' },
  { value: 'Saturday', label: 'Thứ 7' },
  { value: 'Sunday', label: 'Chủ nhật' },
];

export const VOUCHER_CONFIG_KEYS = ['Voucher:MaxPerBill', 'Voucher:ExpiryNotifyBeforeDays'] as const;
export const VOUCHER_MAX_PER_BILL_KEY = 'Voucher:MaxPerBill';

/**
 * Trần hiệu lực của món quà: ô riêng thắng, trống thì lấy `Voucher:MaxPerBill` từ API cấu hình.
 * Trả `undefined` khi chưa biết (ô trống + chưa tải được config) — không suy thành 1.
 */
export function effectiveMaxPerBill(
  formMaxPerBill: number | null | undefined,
  systemMaxPerBill: number | undefined,
): number | undefined {
  if (formMaxPerBill != null) return formMaxPerBill;
  return systemMaxPerBill;
}

/**
 * BR-403 + BR-414: `allowStacking` chỉ chạy bên trong trần. Trần = 1 thì cờ không bao giờ tới lượt.
 * `systemMaxPerBill` đọc từ API, không hardcode số 1.
 */
export function stackingIsIneffective(
  allowStacking: boolean,
  formMaxPerBill: number | null | undefined,
  systemMaxPerBill: number | undefined,
): boolean {
  if (!allowStacking) return false;
  return effectiveMaxPerBill(formMaxPerBill, systemMaxPerBill) === 1;
}

export const emptyConditions = (): VoucherConditions => ({
  branchIds: [],
  validDaysOfWeek: [],
  excludedDates: [],
  allowStacking: false,
});

/** Tóm tắt điều kiện hiển thị trên bảng — không tự suy diễn logic server. */
export function summarizeConditions(conditions: VoucherConditions): string {
  const parts: string[] = [];

  if (conditions.minBillAmount != null) {
    parts.push(`HĐ tối thiểu ${conditions.minBillAmount.toLocaleString('vi-VN')}đ`);
  }
  if (conditions.maxPerBill != null) {
    parts.push(`Tối đa ${conditions.maxPerBill} voucher/HĐ`);
  }
  if (conditions.allowStacking) {
    parts.push('Cho phép dùng chung');
  }
  if (conditions.validFromHour != null && conditions.validToHour != null) {
    parts.push(`${conditions.validFromHour}:00–${conditions.validToHour}:00`);
  }
  if (conditions.validDaysOfWeek.length > 0) {
    parts.push(`${conditions.validDaysOfWeek.length} ngày/tuần`);
  }
  if (conditions.branchIds.length > 0) {
    parts.push(`${conditions.branchIds.length} chi nhánh`);
  }
  if (conditions.excludedDates.length > 0) {
    parts.push(`${conditions.excludedDates.length} ngày loại trừ`);
  }
  if (conditions.fixedExpiresAt) {
    parts.push('Hạn cố định');
  }

  return parts.length > 0 ? parts.join(' · ') : 'Không giới hạn thêm';
}

/** Cột mệnh giá / giá trị — ba ô `S4-BE-05` hiện ở đây, không nhét vào điều kiện. */
export function summarizeRewardValue(
  reward: Pick<
    Reward,
    'kind' | 'discountAmount' | 'discountPercent' | 'maxDiscountAmount' | 'itemValue'
  >,
): string {
  if (reward.kind === 'Discount' && reward.discountAmount != null) {
    return `${reward.discountAmount.toLocaleString('vi-VN')}đ`;
  }
  if (reward.kind === 'PercentDiscount') {
    const parts: string[] = [];
    if (reward.discountPercent != null) parts.push(`${reward.discountPercent}%`);
    if (reward.maxDiscountAmount != null) {
      parts.push(`trần ${reward.maxDiscountAmount.toLocaleString('vi-VN')}đ`);
    }
    return parts.length > 0 ? parts.join(' · ') : '—';
  }
  if (reward.itemValue != null) {
    return `Quy đổi ${reward.itemValue.toLocaleString('vi-VN')}đ`;
  }
  return '—';
}

export function rewardStatusLabel(reward: Reward): { label: string; tone: 'success' | 'default' | 'warning' } {
  if (!reward.isActive) return { label: 'Đang tắt', tone: 'default' };
  if (
    rewardGroupOf(reward.kind) === 'Money' &&
    reward.stockRemaining !== null &&
    reward.stockRemaining <= 10
  ) {
    return { label: 'Sắp hết tồn', tone: 'warning' };
  }
  return { label: 'Đang bật', tone: 'success' };
}

export const rewardApi = {
  list: () => unwrap(apiRequest<Reward[]>('/admin/rewards')),

  create: (body: UpsertRewardPayload) =>
    unwrap(apiRequest<Reward>('/admin/rewards', { method: 'POST', body })),

  update: (id: string, body: UpsertRewardPayload) =>
    unwrap(apiRequest<Reward>(`/admin/rewards/${id}`, { method: 'PUT', body })),
};
